import React, { useEffect, useMemo, useState } from 'react';
import { organizationsService, type Organization } from '@/api/services/organizations';
import { billingAdminService, type BillingPlan } from '@/api/services/billingAdmin';
import { CUSTOMER_WORKSPACE_ROLE_OPTIONS } from '@/utils/workspaceRoles';
import { formatApiError } from '@/utils/formatApiError';
import type { PendingUser } from '@/api/services/userApproval';

type OrgMode = 'existing' | 'create';

type Props = {
  applicant: PendingUser | null;
  open: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (payload: {
    organizationId?: string;
    organizationName?: string;
    organizationLabel: string;
    membershipRole: string;
    planSlug?: string;
  }) => void | Promise<void>;
};

const DEFAULT_PLANS: BillingPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    slug: 'starter',
    priceCents: 0,
    currency: 'EUR',
    billingInterval: 'month',
    maxSearches: 5,
    isActive: true,
  },
];

export default function ApproveApplicantModal({
  applicant,
  open,
  submitting = false,
  onCancel,
  onConfirm,
}: Props) {
  const [orgMode, setOrgMode] = useState<OrgMode>('existing');
  const [orgSearch, setOrgSearch] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [membershipRole, setMembershipRole] = useState('subscriber');
  const [planSlug, setPlanSlug] = useState('starter');
  const [plans, setPlans] = useState<BillingPlan[]>(DEFAULT_PLANS);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOrgMode('existing');
    setOrgSearch('');
    setSelectedOrgId('');
    setNewOrgName('');
    setMembershipRole('subscriber');
    setPlanSlug('starter');
    setError(null);
  }, [open, applicant?.id]);

  useEffect(() => {
    if (!open || orgMode !== 'create') return;
    let cancelled = false;
    billingAdminService
      .listPlans(false)
      .then((res) => {
        if (cancelled) return;
        const active = (res.items ?? []).filter((plan) => plan.isActive && plan.slug !== 'internal');
        setPlans(active.length ? active : DEFAULT_PLANS);
        setPlanSlug((current) =>
          active.some((plan) => plan.slug === current) ? current : active[0]?.slug ?? 'starter',
        );
      })
      .catch(() => {
        if (!cancelled) setPlans(DEFAULT_PLANS);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgMode]);

  useEffect(() => {
    if (!open || orgMode !== 'existing') return;
    let cancelled = false;
    setOrgsLoading(true);
    organizationsService
      .platformList(orgSearch.trim() || undefined)
      .then((res) => {
        if (!cancelled) {
          setOrganizations(
            (res.items ?? []).filter(
              (org) =>
                org.status === 'active' &&
                !org.isInternal &&
                org.workspaceType !== 'internal',
            ),
          );
        }
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err, 'Failed to load organizations'));
      })
      .finally(() => {
        if (!cancelled) setOrgsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgMode, orgSearch]);

  const busy = submitting || working;
  const selectedOrg = useMemo(
    () => organizations.find((org) => org.id === selectedOrgId) ?? null,
    [organizations, selectedOrgId],
  );

  const canSubmit =
    Boolean(applicant) &&
    !busy &&
    ((orgMode === 'existing' && Boolean(selectedOrgId)) ||
      (orgMode === 'create' && Boolean(newOrgName.trim()) && Boolean(planSlug)));

  if (!open || !applicant) return null;

  async function handleConfirm() {
    if (!canSubmit) return;
    setWorking(true);
    setError(null);
    try {
      const payload =
        orgMode === 'existing'
          ? {
              organizationId: selectedOrgId,
              organizationLabel: selectedOrg?.name ?? 'the selected workspace',
              membershipRole,
            }
          : {
              organizationName: newOrgName.trim(),
              organizationLabel: newOrgName.trim(),
              membershipRole,
              planSlug,
            };
      await onConfirm(payload);
    } catch (err) {
      setError(formatApiError(err, 'Approval failed'));
    } finally {
      setWorking(false);
    }
  }

  const confirmOrgName = orgMode === 'existing' ? selectedOrg?.name ?? 'the selected workspace' : newOrgName.trim();

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="modal card card-elevated confirm-action-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="approve-applicant-title"
      >
        <h2 id="approve-applicant-title">Approve applicant</h2>
        <div className="modal-lead">
          <p>
            <strong>{applicant.name || 'Unnamed applicant'}</strong>
            <br />
            <span className="mono">{applicant.email || '—'}</span>
            {applicant.createdAt ? (
              <>
                <br />
                <span className="muted">Registered {new Date(applicant.createdAt).toLocaleString()}</span>
              </>
            ) : null}
          </p>
        </div>

        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="muted" style={{ marginBottom: 8 }}>
            Organization assignment
          </legend>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input
              type="radio"
              name="org-mode"
              checked={orgMode === 'existing'}
              disabled={busy}
              onChange={() => setOrgMode('existing')}
            />
            Existing organization
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <input
              type="radio"
              name="org-mode"
              checked={orgMode === 'create'}
              disabled={busy}
              onChange={() => setOrgMode('create')}
            />
            Create new organization
          </label>
        </fieldset>

        {orgMode === 'existing' ? (
          <>
            <label className="confirm-prompt-modal__field">
              Search organizations
              <input
                type="search"
                value={orgSearch}
                disabled={busy}
                onChange={(e) => setOrgSearch(e.target.value)}
                placeholder="Filter by name or slug"
              />
            </label>
            <label className="confirm-prompt-modal__field">
              Organization
              <select
                value={selectedOrgId}
                disabled={busy || orgsLoading}
                required
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="">Select an organization…</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                    {org.slug ? ` (${org.slug})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="confirm-prompt-modal__field">
            New organization name
            <input
              value={newOrgName}
              disabled={busy}
              required
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Acme GmbH"
            />
          </label>
        )}

        <label className="confirm-prompt-modal__field">
          Workspace role
          <select value={membershipRole} disabled={busy} onChange={(e) => setMembershipRole(e.target.value)}>
            {CUSTOMER_WORKSPACE_ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {orgMode === 'create' ? (
          <label className="confirm-prompt-modal__field">
            Initial plan
            <select value={planSlug} disabled={busy} onChange={(e) => setPlanSlug(e.target.value)}>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.slug}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="muted">The selected workspace keeps its current plan.</p>
        )}

        <p className="muted">
          This will approve the applicant and add them to {confirmOrgName || 'the selected workspace'}.
        </p>

        {error ? <div className="alert alert-error">{error}</div> : null}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={() => void handleConfirm()}>
            {busy ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}
